import { Dispatch } from 'react';
import { FacephiVoiceWidget } from '@facephi/sdk-web-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useLogger, useSendAsset } from '../../hooks';
import { useVocalAuth } from '../../providers';
import {
  RoutesName,
  voiceValidationCustomState,
  VoiceVerifList,
  voiceValidationTypeState,
  VoiceValidationType,
  userNameState,
  userLastNameState,
} from '../../states';
import { getAssetFromBlob, formatBase64Asset } from '../../utils';

type VoiceWidgetProps = {
  setLoading: Dispatch<boolean>;
  transfer: boolean;
};

export const VoiceRecordWidget = ({
  setLoading,
  transfer,
}: VoiceWidgetProps) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const userName = useRecoilValue(userNameState);
  const userLastName = useRecoilValue(userLastNameState);

  const { template, onSaveTemplate } = useVocalAuth();
  const { registerAudio, authenticateAudio } = useSendAsset();

  const { captureException, captureMessage } = useLogger();

  const vocalValidationType = useRecoilValue(voiceValidationTypeState);
  const vocalCustomSentence = useRecoilValue(voiceValidationCustomState);

  const addSentence = (sentence: string): string[] => {
    const limit = transfer ? 1 : 3;
    const vocalSentences: string[] = [];
    for (let i = 0; i < limit; i++) {
      vocalSentences.push(sentence);
    }
    return vocalSentences;
  };

  const getSentences = (): string[] => {
    if (vocalValidationType === VoiceValidationType.custom) {
      return addSentence(vocalCustomSentence);
    } else if (vocalValidationType === VoiceValidationType.nameLastname) {
      return addSentence(`${userName} ${userLastName}`);
    } else {
      return addSentence(t(VoiceVerifList[vocalValidationType].title));
    }
  };

  const onRecordingFinish = async (event: CustomEvent<Blob[]>) => {
    setLoading(true);
    try {
      const base64Records = await getAssetFromBlob(event.detail);
      const formattedAsset = formatBase64Asset(base64Records);

      if (transfer) {
        // Vocal authentication

        await authenticateAudio(formattedAsset[0], template as string);
      } else {
        if (formattedAsset.length) {
          // Vocal registration

          const template = await registerAudio(base64Records);
          if (template) {
            onSaveTemplate(template);
            navigate(RoutesName.successfulRegister);
          } else {
            captureMessage('Voice registration denied');
            navigate(RoutesName.deniedRegister);
          }
        }
      }
    } catch (error) {
      captureMessage(`Error sending audio asset: ${error}`);
      captureException(error as Error);
      if (transfer) {
        navigate(RoutesName.transferDenied);
      }
      navigate(RoutesName.deniedRegister);
    }
  };

  const onExceptionError = async (event: CustomEvent<unknown>) => {
    captureMessage(`Voice Widget Exception: ${event}`);
    if (transfer) {
      navigate(RoutesName.transferDenied);
    } else {
      navigate(RoutesName.deniedRegister);
    }
  };

  const onUserCancel = async () => {
    if (transfer) {
      navigate(RoutesName.transferDenied);
    } else {
      navigate(RoutesName.deniedRegister);
    }
  };

  return (
    <FacephiVoiceWidget
      className="sdk-widget voice-widget"
      bundlePath={`${process.env.PUBLIC_URL}voice`}
      language={i18n.language.split('-')[0]}
      sentences={getSentences()}
      onUserCancel={onUserCancel}
      onRecordingFinish={onRecordingFinish}
      onExceptionError={onExceptionError}
      showUserCancel={true}
    />
  );
};
